import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { FileText, Plus, Save, X } from 'lucide-react';
import { cn } from '../ui/utils';
import { toast } from 'sonner';

interface WellNote {
    id: string;
    text: string;
    type: 'location' | 'maintenance' | 'general';
    createdAt: string;
    createdBy: string;
}

interface WellNotesTabProps {
    notes: WellNote[];
    onAddNote: (note: string) => void;
    isDarkMode: boolean;
}

export function WellNotesTab({ notes, onAddNote, isDarkMode }: WellNotesTabProps) {
    const [isAdding, setIsAdding] = useState(false);
    const [newNote, setNewNote] = useState('');

    const handleSave = () => {
        if (!newNote.trim()) return;
        onAddNote(newNote);
        setNewNote('');
        setIsAdding(false);
        toast.success('Note added');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className={cn("text-base font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>
                    Field Notes
                </h3>
                <Button
                    size="sm"
                    onClick={() => setIsAdding(true)}
                    className={isAdding ? "hidden" : "bg-blue-600 hover:bg-blue-700 text-white"}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Note
                </Button>
            </div>

            {isAdding && (
                <Card className={cn("animate-in fade-in slide-in-from-top-2", isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                    <CardContent className="p-4 space-y-4">
                        <Textarea
                            placeholder="Enter note details..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className={cn("resize-none", isDarkMode ? "bg-slate-800 border-slate-700 text-white" : "")}
                            rows={4}
                        />
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setIsAdding(false);
                                    setNewNote('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={handleSave}
                                disabled={!newNote.trim()}
                            >
                                <Save className="w-4 h-4 mr-2" />
                                Save Note
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-3">
                {notes.length === 0 ? (
                    <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                        <CardContent className="py-12 flex flex-col items-center justify-center text-center">
                            <div className={cn("p-4 rounded-full mb-4", isDarkMode ? "bg-slate-800 text-slate-500" : "bg-slate-100 text-slate-400")}>
                                <FileText className="w-8 h-8" />
                            </div>
                            <p className={cn("text-sm font-medium", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                                No notes found
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    notes.map(note => (
                        <Card
                            key={note.id}
                            className={cn(
                                "border-l-4",
                                isDarkMode ? "bg-slate-900 border-r-slate-800 border-y-slate-800 border-l-blue-500" : "bg-white border-slate-200 border-l-blue-500"
                            )}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex gap-2">
                                        <Badge variant="secondary" className={cn(
                                            "capitalize",
                                            note.type === 'maintenance'
                                                ? "bg-amber-100 text-amber-700 hover:bg-amber-100"
                                                : "bg-blue-100 text-blue-700 hover:bg-blue-100"
                                        )}>
                                            {note.type}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground pt-1">
                                            {new Date(note.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                                <p className={cn("text-sm leading-relaxed whitespace-pre-wrap", isDarkMode ? "text-slate-300" : "text-slate-700")}>
                                    {note.text}
                                </p>
                                <div className="mt-3 text-xs text-muted-foreground">
                                    Added by {note.createdBy}
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
}
